import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, Search, HandHeart, Handshake, Cpu, BarChart3,
  ShieldCheck, Lock, FileCheck, Eye, Users2, Activity, Globe2,
  Building2, HeartHandshake, PlayCircle,
} from "lucide-react";
import { api } from "../lib/api";
import { useI18n } from "../i18n";
import { useSettings } from "../context/SettingsContext";
import { Container, SectionLabel } from "../components/ui-kit";
import ProgramIcon from "../components/ProgramIcon";
import CampaignCard from "../components/CampaignCard";
import AnimatedCounter from "../components/AnimatedCounter";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/7e10f933-be32-4f7a-b273-ca5f06f1a34e/images/a3efdd77f6cd6456c67ff20e0129e1050bcf1eff47fe0d225d53a5ec02f35b03.jpeg";
const CSR_IMG = "https://images.unsplash.com/photo-1714974528737-3e6c7e4d11af?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const NETWORK = [
  ["People", Users2], ["Organizations", Building2], ["Technology", Cpu], ["Communities", HeartHandshake], ["Impact", Globe2],
];

const STEPS = [
  { n: "01", title: "Identify", icon: Search, text: "We identify meaningful social needs and opportunities." },
  { n: "02", title: "Partner", icon: Handshake, text: "We collaborate with appropriate organisations and implementation partners." },
  { n: "03", title: "Enable", icon: Cpu, text: "Technology helps coordinate participation, payments where authorised, documentation and reporting." },
  { n: "04", title: "Measure", icon: BarChart3, text: "We track outcomes and communicate measurable impact." },
];

const TECH = [
  ["Digital campaign management", "Structured, transparent coordination of every initiative."],
  ["Secure payments", "Server-verified transactions with digital receipts."],
  ["Partner management", "Due-diligence, verification and agreement tracking."],
  ["Impact tracking", "Measurable outcomes documented and reported."],
  ["Reporting & analytics", "Clear reporting for partners and supporters."],
  ["Transparency", "Verifiable records and receipt verification."],
];

const TRUST = [
  [Lock, "Secure Payments"], [Handshake, "Responsible Partnerships"], [Eye, "Transparent Reporting"],
  [ShieldCheck, "Data Protection"], [Activity, "Impact Tracking"], [FileCheck, "Verified Partners"],
];

export default function Home() {
  const { t } = useI18n();
  const { settings } = useSettings();
  const [programs, setPrograms] = useState([]);
  const [campaigns, setCampaigns] = useState([]);

  useEffect(() => {
    api.get("/programs").then((r) => setPrograms(r.data)).catch(() => {});
    api.get("/campaigns").then((r) => setCampaigns(r.data)).catch(() => {});
  }, []);

  const countersEnabled = settings?.counters_enabled;
  const counters = settings?.impact_counters || [];

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden bg-[#022B25]">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Global community impact" className="ken-burns h-full w-full object-cover object-center" />
        </div>
        <div className="absolute inset-0 hero-cinematic" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#022B25] via-[#022B25]/45 to-[#022B25]/25 lg:hidden" />
        <Container className="relative pb-16 pt-28 sm:pt-36 lg:pb-24 lg:pt-44">
          <motion.div initial={{ opacity: 0, y: 26 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }} className="max-w-3xl">
            <div className="inline-flex items-center gap-2.5">
              <span className="h-px w-8 bg-gold" />
              <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-gold-light sm:text-xs sm:tracking-[0.26em]">Helping Hands Dubai · Global CSR Platform</span>
            </div>
            <h1 className="mt-6 font-display text-[2.75rem] font-bold leading-[1.05] text-white sm:text-6xl lg:text-[76px]">
              Purpose,<br /><span className="text-gold-light">Powered by People.</span>
            </h1>
            <p className="mt-5 font-display text-lg italic text-emerald-50/90 sm:text-2xl">Technology. Giving. Partnerships. Impact.</p>
            <p className="mt-5 max-w-xl text-[15px] leading-relaxed text-emerald-50/80 sm:mt-6 sm:text-lg">Helping Hands Dubai connects responsible organizations, technology and communities to turn meaningful intentions into measurable impact.</p>
            <div className="mt-8 flex flex-col gap-3 sm:mt-9 sm:flex-row sm:flex-wrap sm:gap-4">
              <Link to="/programs" data-testid="hero-explore-cta" className="group inline-flex items-center justify-center gap-2 rounded-full bg-gold px-7 py-3.5 text-sm font-bold uppercase tracking-wider text-white transition hover:bg-gold-light sm:px-8">
                Support an Initiative <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1 rtl:rotate-180" />
              </Link>
              <Link to="/impact" data-testid="hero-support-cta" className="inline-flex items-center justify-center gap-2 rounded-full border border-white/25 bg-white/5 px-7 py-3.5 text-sm font-bold uppercase tracking-wider text-white backdrop-blur transition hover:bg-white/[0.14] sm:px-8">
                Discover Our Impact <ArrowRight className="h-4 w-4 rtl:rotate-180" />
              </Link>
            </div>
            <p className="mt-8 text-[11px] font-semibold uppercase tracking-[0.34em] text-emerald-100/60">Dubai · UAE · Global</p>
          </motion.div>

          {/* Helping Hands Network motif */}
          <div className="relative mt-16 hidden lg:block" data-testid="hh-network">
            <div className="absolute inset-x-8 top-7 h-px bg-gradient-to-r from-transparent via-gold/40 to-transparent" />
            <div className="relative flex items-center justify-between">
              {NETWORK.map(([label, Icon], i) => (
                <div key={label} className="node-pulse flex flex-col items-center gap-3" style={{ animationDelay: `${i * 0.55}s` }}>
                  <div className="flex h-14 w-14 items-center justify-center rounded-full border border-gold/40 bg-[#022B25]/70 text-gold-light backdrop-blur">
                    <Icon className="h-6 w-6" />
                  </div>
                  <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-emerald-50/80">{label}</span>
                </div>
              ))}
            </div>
          </div>
        </Container>
      </section>

      {/* IMPACT COUNTERS */}
      <section className="border-b border-emerald-900/10 bg-white" data-testid="animated-impact-counters">
        <Container className="py-14">
          {countersEnabled ? (
            <div className="grid grid-cols-2 gap-6 md:grid-cols-3 lg:grid-cols-6">
              {counters.map((c) => (
                <div key={c.label} className="text-center">
                  <div className="font-display text-4xl font-bold text-emerald-900"><AnimatedCounter value={c.value} suffix={c.suffix} /></div>
                  <div className="mt-1 text-xs font-medium uppercase tracking-wider text-slate-500">{c.label}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center gap-3 py-4 text-center">
              <HandHeart className="h-8 w-8 text-gold" />
              <p className="font-display text-3xl font-bold text-emerald-900">{t("building_impact")}</p>
              <p className="max-w-xl text-sm text-slate-500">Verified impact metrics will be published here as our initiatives progress with trusted partners.</p>
            </div>
          )}
        </Container>
      </section>

      {/* FOCUS AREAS */}
      <section className="bg-sand">
        <Container className="py-20 lg:py-28">
          <div className="max-w-2xl">
            <SectionLabel>{t("focus_title")}</SectionLabel>
            <h2 className="mt-4 font-display text-4xl font-bold text-emerald-900 lg:text-5xl">Where we create meaningful impact</h2>
          </div>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {programs.map((p, i) => (
              <motion.div
                key={p.slug}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ delay: i * 0.05, duration: 0.5 }}
              >
                <Link to={`/programs/${p.slug}`} data-testid={`focus-card-${p.slug}`} className="group flex h-full flex-col rounded-2xl border border-emerald-900/10 bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:border-gold/40 hover:hh-shadow">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-50 text-emerald-800 transition group-hover:bg-gold group-hover:text-white">
                    <ProgramIcon name={p.icon} />
                  </div>
                  <h3 className="mt-5 font-display text-xl font-bold text-emerald-900">{p.title}</h3>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-600">{p.description}</p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-gold">{t("focus_explore")} <ArrowRight className="h-4 w-4 rtl:rotate-180" /></span>
                </Link>
              </motion.div>
            ))}
          </div>
        </Container>
      </section>

      {/* FEATURED INITIATIVES */}
      <section className="bg-white">
        <Container className="py-20 lg:py-28">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div className="max-w-2xl">
              <SectionLabel>{t("featured_title")}</SectionLabel>
              <h2 className="mt-4 font-display text-4xl font-bold text-emerald-900 lg:text-5xl">Initiatives with our partners</h2>
            </div>
            <Link to="/programs" className="inline-flex items-center gap-2 text-sm font-bold text-emerald-800 hover:text-gold">View all <ArrowRight className="h-4 w-4 rtl:rotate-180" /></Link>
          </div>
          <div className="mt-12 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {campaigns.slice(0, 3).map((c) => <CampaignCard key={c.slug} c={c} />)}
          </div>
        </Container>
      </section>

      {/* CSR PARTNERSHIP */}
      <section className="bg-emerald-950 text-white">
        <Container className="grid items-center gap-14 py-20 lg:grid-cols-2 lg:py-28">
          <div>
            <SectionLabel light>CSR & Partnerships</SectionLabel>
            <h2 className="mt-4 font-display text-4xl font-bold lg:text-5xl">Make CSR part of your business impact</h2>
            <p className="mt-5 text-lg leading-relaxed text-emerald-50/80">Helping Hands enables businesses to turn CSR commitments into structured, measurable and transparent social-impact initiatives.</p>
            <div className="mt-8 grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2">
              {["CSR campaign design", "Employee engagement", "Corporate volunteering", "Impact tracking", "Program coordination", "Digital reporting", "Impact documentation", "Partner coordination"].map((f) => (
                <div key={f} className="flex items-center gap-2 text-sm text-emerald-50/90"><span className="h-1.5 w-1.5 shrink-0 rounded-full bg-gold-light" /> {f}</div>
              ))}
            </div>
            <Link to="/csr" data-testid="csr-partner-cta" className="mt-9 inline-flex items-center gap-2 rounded-full bg-gold px-7 py-3.5 text-sm font-bold text-white transition hover:bg-gold-light">
              {t("cta_partner")} <ArrowRight className="h-4 w-4 rtl:rotate-180" />
            </Link>
          </div>
          <div className="relative">
            <img src={CSR_IMG} alt="Corporate partnership" className="rounded-3xl object-cover hh-shadow-lg" />
            <div className="absolute -bottom-6 -start-6 hidden rounded-2xl border border-white/10 bg-emerald-900/90 p-5 backdrop-blur sm:block">
              <Globe2 className="h-6 w-6 text-gold-light" />
              <p className="mt-2 max-w-[180px] text-sm font-medium">CSR without borders — impact across regions.</p>
            </div>
          </div>
        </Container>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-sand">
        <Container className="py-20 lg:py-28">
          <div className="max-w-2xl">
            <SectionLabel>{t("how_title")}</SectionLabel>
            <h2 className="mt-4 font-display text-4xl font-bold text-emerald-900 lg:text-5xl">From good intentions to measurable action</h2>
          </div>
          <div className="mt-14 grid gap-8 md:grid-cols-2 lg:grid-cols-4">
            {STEPS.map((s, i) => (
              <div key={s.n} className="relative">
                <div className="flex items-center gap-3">
                  <span className="font-display text-5xl font-bold text-gold/40">{s.n}</span>
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-900 text-white"><s.icon className="h-5 w-5" /></div>
                </div>
                <h3 className="mt-4 font-display text-2xl font-bold text-emerald-900">{s.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{s.text}</p>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* TECHNOLOGY */}
      <section className="bg-white">
        <Container className="py-20 lg:py-28">
          <div className="max-w-2xl">
            <SectionLabel>Technology</SectionLabel>
            <h2 className="mt-4 font-display text-4xl font-bold text-emerald-900 lg:text-5xl">Technology that makes impact visible</h2>
            <p className="mt-4 text-lg text-slate-600">A legitimate technology backbone — not just a donation collector.</p>
          </div>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {TECH.map(([title, text]) => (
              <div key={title} className="rounded-2xl border border-emerald-900/10 bg-sand p-6">
                <h3 className="font-display text-xl font-bold text-emerald-900">{title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{text}</p>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* TRUST */}
      <section className="border-y border-emerald-900/10 bg-sand" data-testid="trust-indicators-row">
        <Container className="py-12">
          <div className="grid grid-cols-2 gap-6 md:grid-cols-3 lg:grid-cols-6">
            {TRUST.map(([Icon, label]) => (
              <div key={label} className="flex flex-col items-center gap-2 text-center">
                <Icon className="h-7 w-7 text-emerald-800" />
                <span className="text-xs font-semibold text-slate-600">{label}</span>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* ISLAMIC GIVING TEASER */}
      <section className="islamic-pattern text-white">
        <Container className="grid items-center gap-12 py-20 lg:grid-cols-2 lg:py-28">
          <div>
            <SectionLabel light>Islamic Giving</SectionLabel>
            <h2 className="mt-4 font-display text-4xl font-bold lg:text-5xl">Plan your Zakat with confidence</h2>
            <p className="mt-5 text-lg leading-relaxed text-emerald-50/80">Tools and resources to help you understand and plan your Zakat and voluntary giving — presented with respect and clarity.</p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/zakat-calculator" data-testid="islamic-zakat-cta" className="inline-flex items-center gap-2 rounded-full bg-gold px-7 py-3.5 text-sm font-bold text-white transition hover:bg-gold-light">{t("cta_zakat")} <ArrowRight className="h-4 w-4 rtl:rotate-180" /></Link>
              <Link to="/islamic-giving" className="inline-flex items-center gap-2 rounded-full border border-white/30 px-7 py-3.5 text-sm font-bold text-white transition hover:bg-white/10">Islamic Giving Guide</Link>
            </div>
          </div>
          <Link to="/aayat" data-testid="home-aayat-cta" className="group block rounded-3xl border border-gold/20 bg-emerald-900/40 p-8 backdrop-blur transition hover:border-gold/50 hover:bg-emerald-900/55">
            <PlayCircle className="h-8 w-8 text-gold-light" />
            <p className="mt-4 text-2xl leading-[1.9] text-emerald-50" dir="rtl" style={{ fontFamily: "'Amiri', serif" }}>وَمَنْ أَحْيَاهَا فَكَأَنَّمَا أَحْيَا النَّاسَ جَمِيعًا</p>
            <p className="mt-4 font-display text-xl italic text-emerald-50/90">Listen to the Aayat Library</p>
            <p className="mt-2 text-sm text-emerald-100/70">Authentic recitation of foundational verses on charity, mercy and humanity — with English translation.</p>
            <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-bold uppercase tracking-wider text-gold-light">Explore <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1 rtl:rotate-180" /></span>
          </Link>
        </Container>
      </section>

      {/* STORIES OF RESILIENCE */}
      <section className="bg-white">
        <Container className="py-20 lg:py-28">
          <div className="max-w-2xl">
            <SectionLabel>Dignity First</SectionLabel>
            <h2 className="mt-4 font-display text-4xl font-bold text-emerald-900 lg:text-5xl">Stories of resilience, not just need</h2>
            <p className="mt-4 text-lg text-slate-600">Behind every initiative is a human being whose life is changed through responsible partnership and support.</p>
          </div>
          <div className="mt-12 grid gap-8 md:grid-cols-2">
            {[
              { img: "1760873059715-7c7cfbe2a2c6", quote: "The well in our village means my daughters can go to school instead of walking six hours for water.", who: "Amina", where: "Sudan" },
              { img: "1547496614-d145e2fa88ed", quote: "After the floods, the shelter and relief kit gave my family a roof and dignity when we had lost everything.", who: "Yusuf", where: "Pakistan" },
            ].map((s) => (
              <div key={s.who} className="group relative overflow-hidden rounded-3xl">
                <img src={`https://images.unsplash.com/photo-${s.img}?crop=entropy&cs=srgb&fm=jpg&q=85&w=900`} alt={`${s.who}, ${s.where}`} className="h-80 w-full object-cover transition duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-emerald-950/90 via-emerald-950/30 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-7 text-white">
                  <p className="font-display text-2xl leading-snug">"{s.quote}"</p>
                  <p className="mt-3 text-sm font-semibold text-gold-light">— {s.who}, {s.where}</p>
                </div>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* FINAL EMOTIONAL CTA */}
      <section className="relative overflow-hidden bg-emerald-950 text-white">
        <img src="https://images.unsplash.com/photo-1596657306581-e88c58b34723?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600" alt="" className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="absolute inset-0 islamic-pattern opacity-40" />
        <Container className="relative py-24 text-center">
          <h2 className="mx-auto max-w-3xl font-display text-4xl font-bold leading-tight lg:text-5xl">Somewhere in the world, someone is waiting for a helping hand.</h2>
          <p className="mx-auto mt-5 max-w-2xl text-lg text-emerald-50/80">Your contribution can become a restored place of worship, a stronger community, an opportunity for a family, or hope where it is needed most.</p>
          <div className="mt-9 flex flex-wrap justify-center gap-4">
            <Link to="/programs" className="inline-flex items-center gap-2 rounded-full bg-gold px-8 py-4 text-sm font-bold text-white transition hover:bg-gold-light">Support an Initiative <ArrowRight className="h-4 w-4 rtl:rotate-180" /></Link>
            <Link to="/csr" className="inline-flex items-center gap-2 rounded-full border border-white/30 px-8 py-4 text-sm font-bold text-white transition hover:bg-white/10">Become a CSR Partner</Link>
          </div>
        </Container>
      </section>

      {/* PARTNER CTA */}
      <section className="bg-emerald-950 text-white">
        <Container className="py-16 text-center">
          <h2 className="mx-auto max-w-3xl font-display text-3xl font-bold lg:text-4xl">Are you an organisation creating positive change?</h2>
          <p className="mx-auto mt-4 max-w-2xl text-emerald-50/80">Let's explore how technology, CSR and responsible partnerships can amplify your impact.</p>
          <Link to="/csr" className="mt-8 inline-flex items-center gap-2 rounded-full bg-gold px-8 py-4 text-sm font-bold text-white transition hover:bg-gold-light">{t("cta_partner")} <ArrowRight className="h-4 w-4 rtl:rotate-180" /></Link>
        </Container>
      </section>
    </div>
  );
}
